import React from 'react';
import LegalPageLayout, { AccordionSection } from './LegalPageLayout';

const Security = () => (
  <LegalPageLayout
    title="Security"
    subtitle="At OpticVance, security is not an afterthought it is foundational to everything we build. Learn about our comprehensive security practices."
    lastUpdated="March 3, 2026"
  >
    <AccordionSection title="Infrastructure Security" defaultOpen>
      <p>OpticVance operates on enterprise-grade cloud infrastructure with multiple layers of protection:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li><strong className="text-white">SOC 2 Type II Certified:</strong> Independently audited controls for security, availability, processing integrity, confidentiality, and privacy</li>
        <li><strong className="text-white">ISO 27001 Compliant:</strong> Information security management system that meets international standards</li>
        <li><strong className="text-white">Multi-region deployment:</strong> Data is replicated across geographically distributed data centers for redundancy</li>
        <li><strong className="text-white">DDoS protection:</strong> Automated detection and mitigation of distributed denial-of-service attacks</li>
        <li><strong className="text-white">Network segmentation:</strong> Micro-segmented networks with zero-trust architecture</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Data Encryption">
      <p>All data is encrypted both in transit and at rest using industry-standard encryption algorithms:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li><strong className="text-white">In Transit:</strong> TLS 1.3 for all data transmissions. Certificate pinning enforced on all API endpoints.</li>
        <li><strong className="text-white">At Rest:</strong> AES-256 encryption for all stored data. Encryption keys are managed via hardware security modules (HSMs) with automatic rotation.</li>
        <li><strong className="text-white">Key Management:</strong> Customer-managed encryption keys (CMEK) available for Enterprise plans. Keys never leave the HSM boundary.</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Access Controls">
      <p>We implement strict access controls to ensure only authorized personnel can access systems and data:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li>Role-based access control (RBAC) with principle of least privilege</li>
        <li>Multi-factor authentication (MFA) required for all employees and available for all customers</li>
        <li>Single Sign-On (SSO) integration via SAML 2.0 and OpenID Connect</li>
        <li>Session management with automatic timeout and concurrent session limits</li>
        <li>Privileged access management with just-in-time elevation</li>
        <li>Comprehensive audit logging of all access events</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Application Security">
      <p>Our development practices incorporate security at every stage of the software development lifecycle:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li>Secure code reviews and automated static analysis (SAST) on every pull request</li>
        <li>Dynamic application security testing (DAST) in staging environments</li>
        <li>Dependency scanning for known vulnerabilities in third-party libraries</li>
        <li>Container image scanning before deployment to production</li>
        <li>Regular penetration testing by independent third-party security firms</li>
        <li>Bug bounty program for responsible disclosure of security vulnerabilities</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Incident Response">
      <p>OpticVance maintains a comprehensive incident response plan that includes:</p>
      <ul className="list-disc list-inside space-y-1.5 ml-2">
        <li>24/7 security operations center (SOC) monitoring</li>
        <li>Automated alerting and escalation procedures</li>
        <li>Mean time to detect (MTTD) under 15 minutes for critical incidents</li>
        <li>Customer notification within 72 hours of confirmed data breach</li>
        <li>Post-incident review and continuous improvement process</li>
        <li>Regular tabletop exercises and incident simulation drills</li>
      </ul>
    </AccordionSection>

    <AccordionSection title="Compliance & Certifications">
      <p>OpticVance maintains the following compliance certifications and attestations:</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        {['SOC 2 Type II', 'ISO 27001', 'HIPAA', 'GDPR', 'CCPA', 'PCI DSS Level 1'].map((cert) => (
          <div key={cert} className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
            <svg viewBox="0 0 24 24" fill="none" stroke="#08CB00" strokeWidth="2" className="w-5 h-5 flex-shrink-0">
              <path d="M12 2l8 4v6c0 5.25-3.5 9.74-8 11-4.5-1.26-8-5.75-8-11V6l8-4Z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <span className="text-white font-medium text-sm">{cert}</span>
          </div>
        ))}
      </div>
    </AccordionSection>

    <AccordionSection title="Report a Vulnerability">
      <p>We value the security community's efforts in helping keep OpticVance and our users safe. If you discover a security vulnerability, please report it responsibly:</p>
      <div className="mt-2 p-4 rounded-lg bg-primary/5 border border-primary/10">
        <p className="text-white font-medium">Security Team</p>
        <p>Email: <span className="text-primary">help@opticvance.com</span></p>
        <p>Address: OpticVance Inc., 18 Waverly Pl, New York, NY, USA</p>
        <p>Postal Code: 10003</p>
        <p>Phone: +16468143909</p>
        <p>PGP Key: Available on our security page</p>
        <p className="mt-2 text-xs">We aim to respond to all reports within 24 hours and will keep you updated on our progress.</p>
      </div>
    </AccordionSection>
  </LegalPageLayout>
);

export default Security;
